import {
  Button,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack,
  Skeleton,
  VStack,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { date as commonDate, finleyAssert } from '@finley-technologies/common';

import {
  FundingRequestEntitiesFragmentGQLType,
  ResolvedLoanFragmentGQLType,
  useGetCreditFacilityMetricsQuery,
  useGetFundingRequestQuery,
  FundingRequestWithFullDetailsFieldsFragmentGQLType,
  useGetResolvedLoanByIdQuery,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { LoanPricingOption } from './LoanPricingOption';
import { IdPill } from 'packages/loans/ui/components/IdPill';
import { TaskDetails } from 'packages/tasks/ui/TaskEntityDetails';
import { Icon } from '~/packages/common-frontend/components/icons';
import { Typography } from '~/packages/common-frontend/components/Typography';
import {
  formatCurrencyAmount,
  truncateUuid,
} from '~/packages/common-frontend/utils';
import { Bank } from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { FundingRequestStatusBadge } from '~/packages/funding-requests/ui/shared/StatusBadge';

export const FundingRequestDetails = ({
  loan,
  fundingRequest,
}: {
  loan: ResolvedLoanFragmentGQLType;
  fundingRequest: FundingRequestEntitiesFragmentGQLType;
}) => {
  return (
    <VStack w="100%" gap="1rem">
      <HStack justifyContent="space-between" w="100%">
        <Typography fontWeight="medium">Funding Request ID</Typography>
        <Typography variant="base-muted">
          {truncateUuid(fundingRequest.fundingRequestId)}
        </Typography>
      </HStack>
      <HStack justifyContent="space-between" w="100%">
        <Typography fontWeight="medium">Activity Type</Typography>
        <Typography variant="base-muted">
          {_.startCase(fundingRequest.fundingRequestType)}
        </Typography>
      </HStack>
      <LoanPricingOption loan={loan} loading={false} />
      <HStack justifyContent="space-between" w="100%">
        <Typography fontWeight="medium">Draw Request Amount</Typography>
        <Typography variant="base-muted">
          {formatCurrencyAmount(
            fundingRequest.totalRequestedAmount,
            Bank.Currency.USD,
          )}
        </Typography>
      </HStack>
      <HStack justifyContent="space-between" w="100%">
        <Typography fontWeight="medium">Funding Date</Typography>
        <Typography variant="base-muted">
          {commonDate.fullDateStrToHumanDateString(
            fundingRequest.originationDate,
          )}
        </Typography>
      </HStack>
      <HStack justifyContent="space-between" w="100%">
        <Typography fontWeight="medium">Maturity Date</Typography>
        <Typography variant="base-muted">
          {commonDate.fullDateStrToHumanDateString(loan.dates.maturityDate)}
        </Typography>
      </HStack>
    </VStack>
  );
};

export const FundingRequestDrawer = ({
  isOpen,
  fundingRequestId,
  loan,
  onToggle,
  taskId,
}: {
  isOpen: boolean;
  fundingRequestId: string;
  loan: ResolvedLoanFragmentGQLType;
  onToggle: (isOpen: boolean) => void;
  taskId?: string;
}) => {
  const { data: metricsDataRequest } = useGetCreditFacilityMetricsQuery({
    skip: !loan?.facility,
    variables: {
      creditFacilityId: loan?.facility.creditFacilityId as string,
      postingDate: commonDate.currentDateStr(),
    },
    fetchPolicy: 'no-cache',
  });

  const metrics = metricsDataRequest?.metrics;

  const fundingRequest = loan.funding.requests.find(
    (fr) => fr.fundingRequestId === fundingRequestId,
  );

  finleyAssert(
    fundingRequest,
    `Funding Request must exist [fundingRequestId: ${fundingRequestId}] [loanId: ${loan.id}]`,
  );

  return (
    <Drawer
      isOpen={isOpen}
      placement="right"
      onClose={() => onToggle(false)}
      size="xl"
    >
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader mt="1.5rem" px="3rem" pb="0">
          <Typography variant="section-title" trim>
            Funding Request {truncateUuid(fundingRequest.fundingRequestId)}
          </Typography>
        </DrawerHeader>
        <Divider my="1.5rem" w="100%" />
        <DrawerBody px="3rem">
          <VStack align="flex-start" gap="2rem">
            <Flex>
              <FundingRequestStatusBadge
                status={fundingRequest.fundingRequestStatus}
              />
            </Flex>

            {taskId && <TaskDetails taskId={taskId} />}

            <Divider />
            <Typography variant="section-subtitle" trim>
              Funding Request
            </Typography>
            <FundingRequestDetails
              fundingRequest={fundingRequest}
              loan={loan}
            />

            <Divider />
            <Typography variant="section-subtitle" trim>
              Facility Details
            </Typography>
            <VStack w="100%" gap="1rem">
              <HStack justifyContent="space-between" w="100%">
                <Typography fontWeight="medium">Name</Typography>
                <Typography variant="base-muted">
                  {_.startCase(loan.facility.creditFacilityName)}
                </Typography>
              </HStack>
              <HStack justifyContent="space-between" w="100%">
                <Typography fontWeight="medium">Borrower</Typography>
                <Typography variant="base-muted">
                  {_.startCase(loan.parties.borrowerOrganization.name)}
                </Typography>
              </HStack>
              <HStack justifyContent="space-between" w="100%">
                <Typography fontWeight="medium">Global Commitment</Typography>
                <Typography variant="base-muted">
                  {formatCurrencyAmount(
                    loan.facility.commitmentAmount,
                    Bank.Currency.USD,
                  )}
                </Typography>
              </HStack>
              <HStack justifyContent="space-between" w="100%">
                <Typography fontWeight="medium">Global Principal</Typography>
                {metrics ? (
                  <Typography variant="base-muted">
                    {formatCurrencyAmount(
                      metrics.outstandingPrincipalBalance,
                      Bank.Currency.USD,
                    )}
                  </Typography>
                ) : (
                  <Skeleton w="10rem" h="1rem" />
                )}
              </HStack>
            </VStack>

            <Divider />
            <Flex justify="flex-end" w="100%">
              <Button
                as={RouterLink}
                to={`/funding-requests/${fundingRequest.fundingRequestId}`}
                rightIcon={<Icon icon="chevron-right" />}
                variant="v2-ghost"
              >
                View Funding Request
              </Button>
            </Flex>
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

const FundingRequestMultiLoanDetails = ({
  loan,
}: {
  loan: FundingRequestWithFullDetailsFieldsFragmentGQLType['loans'][0];
}) => {
  const getLoanQuery = useGetResolvedLoanByIdQuery({
    variables: { loanId: loan.id },
    fetchPolicy: 'cache-and-network',
  });

  if (getLoanQuery.error) {
    throw getLoanQuery.error;
  }

  if (!getLoanQuery.data?.loan) {
    return null;
  }

  const resolvedLoan = getLoanQuery.data.loan;

  return (
    <VStack w="100%" gap="1rem">
      <HStack justifyContent="space-between" w="100%">
        <Typography fontWeight="medium">Name</Typography>
        <Typography variant="base-muted">
          {_.startCase(resolvedLoan.name)}
        </Typography>
      </HStack>
      <HStack justifyContent="space-between" w="100%">
        <Typography fontWeight="medium">Borrower</Typography>
        <Typography variant="base-muted">
          {_.startCase(resolvedLoan.parties.borrowerOrganization.name)}
        </Typography>
      </HStack>
      <HStack justifyContent="space-between" w="100%">
        <Typography fontWeight="medium">Global Commitment</Typography>
        <Typography variant="base-muted">
          {formatCurrencyAmount(
            resolvedLoan.facility.commitmentAmount,
            Bank.Currency.USD,
          )}
        </Typography>
      </HStack>
      <HStack justifyContent="space-between" w="100%">
        <Typography fontWeight="medium">Initial Funding Amount</Typography>
        <Typography variant="base-muted">
          {formatCurrencyAmount(
            resolvedLoan.funding.initialFundingAmount,
            Bank.Currency.USD,
          )}
        </Typography>
      </HStack>
      <LoanPricingOption loan={resolvedLoan} loading={false} />
      <HStack justifyContent="space-between" w="100%">
        <Typography fontWeight="medium">Funding Date</Typography>
        <Typography variant="base-muted">
          {commonDate.fullDateStrToHumanDateString(
            resolvedLoan.dates.settledDate ??
              resolvedLoan.dates.targetFundingDate,
          )}
        </Typography>
      </HStack>
      <HStack justifyContent="space-between" w="100%">
        <Typography fontWeight="medium">Maturity Date</Typography>
        <Typography variant="base-muted">
          {commonDate.fullDateStrToHumanDateString(
            resolvedLoan.dates.maturityDate,
          )}
        </Typography>
      </HStack>
    </VStack>
  );
};

export const FundingRequestMultiLoanDrawer = ({
  isOpen,
  fundingRequestId,
  creditFacilityId,
  onToggle,
  taskId,
}: {
  isOpen: boolean;
  fundingRequestId: string;
  creditFacilityId: string;
  onToggle: (isOpen: boolean) => void;
  taskId?: string;
}) => {
  const getFundingRequest = useGetFundingRequestQuery({
    variables: { fundingRequestId },
  });
  const getCreditFacilityMetrics = useGetCreditFacilityMetricsQuery({
    variables: {
      creditFacilityId,
      postingDate: commonDate.currentDateStr(),
    },
  });

  if (getCreditFacilityMetrics.error) {
    throw getCreditFacilityMetrics.error;
  }

  if (getFundingRequest.error) {
    throw getFundingRequest.error;
  }

  if (!getFundingRequest.data?.fundingRequestWithFullDetails) {
    return null;
  }

  const fundingRequest = getFundingRequest.data.fundingRequestWithFullDetails;
  const metrics = getCreditFacilityMetrics.data?.metrics;

  return (
    <Drawer
      isOpen={isOpen}
      placement="right"
      onClose={() => onToggle(false)}
      size="xl"
    >
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader mt="1.5rem" px="3rem" pb="0">
          <Typography variant="section-title" trim>
            Funding Request {truncateUuid(fundingRequest.id)}
          </Typography>
        </DrawerHeader>
        <Divider my="1.5rem" w="100%" />
        <DrawerBody px="3rem">
          <VStack align="flex-start" gap="2rem">
            <FundingRequestStatusBadge status={fundingRequest.status} />

            {taskId && <TaskDetails taskId={taskId} />}

            <Divider />
            <Typography variant="section-subtitle" trim>
              Funding Request
            </Typography>
            <VStack w="100%" gap="1rem">
              <HStack justifyContent="space-between" w="100%">
                <Typography fontWeight="medium">Funding Request ID</Typography>
                <Typography variant="base-muted">
                  {truncateUuid(fundingRequest.id)}
                </Typography>
              </HStack>
              <HStack justifyContent="space-between" w="100%">
                <Typography fontWeight="medium">Activity Type</Typography>
                <Typography variant="base-muted">
                  {_.startCase(fundingRequest.type)}
                </Typography>
              </HStack>
              <HStack justifyContent="space-between" w="100%">
                <Typography fontWeight="medium">Draw Request Amount</Typography>
                <Typography variant="base-muted">
                  {formatCurrencyAmount(
                    fundingRequest.drawRequestTotalAmount,
                    Bank.Currency.USD,
                  )}
                </Typography>
              </HStack>
              <HStack justifyContent="space-between" w="100%">
                <Typography fontWeight="medium">Reporting Date</Typography>
                <Typography variant="base-muted">
                  {commonDate.fullDateStrToHumanDateString(
                    fundingRequest.reportingDate,
                  )}
                </Typography>
              </HStack>
              <HStack justifyContent="space-between" w="100%">
                <Typography fontWeight="medium">Global Principal</Typography>
                {metrics ? (
                  <Typography variant="base-muted">
                    {formatCurrencyAmount(
                      metrics.outstandingPrincipalBalance,
                      Bank.Currency.USD,
                    )}
                  </Typography>
                ) : (
                  <Skeleton w="10rem" h="1rem" />
                )}
              </HStack>
            </VStack>

            {fundingRequest.loans.map((l) => {
              return (
                <React.Fragment key={l.id}>
                  <Divider />
                  <HStack gap="1em">
                    <Typography variant="section-subtitle" trim>
                      Loan Details
                    </Typography>
                    <IdPill
                      to={`/loans/${l.id}`}
                      label=""
                      value={
                        l.externalLoanId
                          ? `${truncateUuid(l.id)} (${l.externalLoanId})`
                          : `${truncateUuid(l.id)}`
                      }
                    />
                  </HStack>
                  <FundingRequestMultiLoanDetails loan={l} />
                </React.Fragment>
              );
            })}

            <Divider />
            <Flex justify="flex-end" w="100%">
              <Button
                as={RouterLink}
                to={`/funding-requests/${fundingRequest.id}`}
                rightIcon={<Icon icon="chevron-right" />}
                variant="v2-ghost"
              >
                View Funding Request
              </Button>
            </Flex>
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
